import React from 'react';
import { MessageStats, MetricCount } from './types';

type Tone = 'success' | 'progress' | 'attention' | 'danger' | 'muted';
type Rank = 'primary' | 'secondary';

function sum(...counts: MetricCount[]): MetricCount {
  return counts.reduce(
    (acc, count) => ({ today: acc.today + count.today, cumulative: acc.cumulative + count.cumulative }),
    { today: 0, cumulative: 0 }
  );
}

function Row({
  label,
  count,
  tone,
  rank
}: {
  label: string;
  count: MetricCount;
  tone: Tone;
  rank: Rank;
}) {
  const zeroDanger = tone === 'danger' && count.today === 0 && count.cumulative === 0;
  return (
    <div className={`stat-row is-${rank} tone-${tone}${zeroDanger ? ' is-zero' : ''}`}>
      <span className="stat-label">{label}</span>
      <span className="stat-count">{count.cumulative}</span>
      <span className="stat-today">今日 {count.today}</span>
    </div>
  );
}

function Glance({
  items
}: {
  items: { label: string; count: MetricCount; tone: Tone }[];
}) {
  return (
    <p className="stat-glance">
      {items.map(item => {
        const quiet = item.tone === 'danger' && item.count.cumulative === 0 && item.count.today === 0;
        return (
          <span key={item.label} className={`stat-glance-item tone-${item.tone}${quiet ? ' is-zero' : ''}`}>
            {item.label}
            <strong>{item.count.cumulative}</strong>
          </span>
        );
      })}
    </p>
  );
}

export default function MessageStatsBoard({
  stats,
  loading,
  error
}: {
  stats: MessageStats | null;
  loading: boolean;
  error: string | null;
}) {
  if (loading && !stats) {
    return (
      <section className="message-stats is-loading" aria-busy="true" aria-label="状态统计">
        <p role="status">正在加载统计…</p>
        <div className="message-stats-skeleton">
          {Array.from({ length: 5 }, (_, index) => (
            <span key={index} className="stat-skel" />
          ))}
        </div>
      </section>
    );
  }
  if (error && !stats) {
    return (
      <section className="message-stats is-error" aria-label="状态统计">
        <p className="message-stats-error">统计加载失败，请点击刷新列表重试</p>
      </section>
    );
  }
  if (!stats) return null;
  const { normalization: n, analysis: a, review: r, publication: p } = stats;
  const published = sum(p.automaticPublished, p.manualPublished);
  return (
    <section className="message-stats" aria-label="状态统计">
      <div className="message-stats-group is-total" role="group" aria-label="快讯总量">
        <h3>快讯总量</h3>
        <strong className="stat-hero-value">{stats.messageTotal.cumulative}</strong>
        <div className="stat-row is-primary tone-muted">
          <span className="stat-label">快讯总数</span>
          <span className="stat-count">累计 {stats.messageTotal.cumulative}</span>
          <span className="stat-today">今日 {stats.messageTotal.today}</span>
        </div>
      </div>
      <div className="message-stats-group" role="group" aria-label="文本规范化">
        <h3>文本规范化</h3>
        <Glance
          items={[
            { label: '完成', count: n.completed, tone: 'success' },
            { label: '待办', count: sum(n.pending, n.running, n.retrying), tone: 'progress' },
            { label: '异常', count: sum(n.rejected, n.failed), tone: 'danger' }
          ]}
        />
        <div className="message-stats-items">
          <Row label="待规范化" count={n.pending} tone="progress" rank="primary" />
          <Row label="规范化中" count={n.running} tone="progress" rank="primary" />
          <Row label="规范化完成" count={n.completed} tone="success" rank="primary" />
          <Row label="规范化未通过" count={n.rejected} tone="attention" rank="primary" />
          <Row label="规范化失败" count={n.failed} tone="danger" rank="primary" />
          <Row label="规范化重试中" count={n.retrying} tone="progress" rank="secondary" />
        </div>
      </div>
      <div className="message-stats-group" role="group" aria-label="AI分析">
        <h3>AI分析</h3>
        <Glance
          items={[
            { label: '完成', count: a.completed, tone: 'success' },
            { label: '待办', count: sum(a.notStarted, a.pending, a.running, a.retrying), tone: 'progress' },
            { label: '异常', count: a.failed, tone: 'danger' }
          ]}
        />
        <div className="message-stats-items">
          <Row label="尚未进入AI分析" count={a.notStarted} tone="muted" rank="secondary" />
          <Row label="待AI分析" count={a.pending} tone="progress" rank="primary" />
          <Row label="AI分析中" count={a.running} tone="progress" rank="primary" />
          <Row label="AI分析已完成" count={a.completed} tone="success" rank="primary" />
          <Row label="AI分析失败" count={a.failed} tone="danger" rank="primary" />
          <Row label="AI分析重试中" count={a.retrying} tone="progress" rank="secondary" />
        </div>
        <div className="message-stats-results" aria-label="AI分析结果">
          <Row label="有效完整" count={a.results.validComplete} tone="success" rank="secondary" />
          <Row label="缺失事实" count={a.results.validWithMissingFacts} tone="attention" rank="secondary" />
          <Row label="信息不足" count={a.results.insufficient} tone="muted" rank="secondary" />
          <Row label="内容无关" count={a.results.irrelevant} tone="muted" rank="secondary" />
        </div>
      </div>
      <div className="message-stats-group" role="group" aria-label="人工审核统计">
        <h3>人工审核</h3>
        <Glance
          items={[
            { label: '待审', count: sum(r.required, r.stale), tone: 'attention' },
            { label: '已审', count: r.reviewed, tone: 'success' },
            { label: '无需审', count: r.notRequired, tone: 'muted' }
          ]}
        />
        <div className="message-stats-items">
          <Row label="需人工审核" count={r.required} tone="attention" rank="primary" />
          <Row label="已人工审核" count={r.reviewed} tone="success" rank="primary" />
          <Row label="需重新审核" count={r.stale} tone="attention" rank="primary" />
          <Row label="无需人工审核" count={r.notRequired} tone="muted" rank="secondary" />
          <Row label="暂无需人工操作" count={r.notReady} tone="muted" rank="secondary" />
        </div>
      </div>
      <div className="message-stats-group" role="group" aria-label="公开状态统计">
        <h3>公开状态</h3>
        <Glance
          items={[
            { label: '已公开', count: published, tone: 'success' },
            { label: '未公开', count: p.unpublished, tone: 'muted' },
            { label: '异常', count: sum(p.failed, p.blocked), tone: 'danger' }
          ]}
        />
        <div className="message-stats-items">
          <Row label="自动公开" count={p.automaticPublished} tone="success" rank="primary" />
          <Row label="人工公开" count={p.manualPublished} tone="success" rank="primary" />
          <Row label="未公开" count={p.unpublished} tone="muted" rank="primary" />
          <Row label="发布失败" count={p.failed} tone="danger" rank="primary" />
          <Row label="已拦截" count={p.blocked} tone="danger" rank="primary" />
          <Row label="发布中" count={p.publishing} tone="progress" rank="secondary" />
          <Row label="已撤回" count={p.withdrawn} tone="muted" rank="secondary" />
        </div>
      </div>
    </section>
  );
}
